import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type {
  GuidedAgeUnit,
  GuidedAnswer,
  GuidedAssessmentPath,
  GuidedMaternalStatus,
  GuidedModeDefinition,
  GuidedModeDemographicItem,
  GuidedModeQuestionDefinition,
  GuidedModeTrack,
  GuidedPatientSex,
} from '../types/guided-mode.types'
import {
  convertAgeToDays,
  isAgeWithinAssessmentPath,
  isValidAge,
  MAX_AGE_BY_UNIT,
  selectAssessmentPath,
  shouldCollectMaternalStatus,
} from '../utils/assessment-selection'
import { findDemographicQuestion } from '../utils/assessment-definition'

const SEX_DISPLAY_ORDER: ReadonlyArray<Exclude<GuidedPatientSex, ''>> = ['male', 'female']

interface GuidedModeDemographicsIdentifier {
  panel: string
  required: boolean
}

interface UseGuidedDemographicsOptions {
  activeAssessmentPath: Ref<GuidedAssessmentPath | null>
  requestedAssessmentPath?: Ref<GuidedAssessmentPath | null>
  assessmentDefinitions: ComputedRef<Record<string, GuidedModeDefinition>>
  identifierConfig: ComputedRef<GuidedModeDemographicsIdentifier | null>
  tracks: ComputedRef<GuidedModeTrack[]>
  translate: (
    token: string,
    fallback: string,
    values?: Record<string, string | number>,
  ) => string
  age: Ref<number | undefined>
  ageUnit: Ref<GuidedAgeUnit>
  sex: Ref<GuidedPatientSex>
  maternalStatus: Ref<GuidedMaternalStatus>
  identifierValue: Ref<string>
}

interface GuidedModeDemographicsSnapshot {
  age: number | undefined
  ageUnit: GuidedAgeUnit
  sex: GuidedPatientSex
  maternalStatus: GuidedMaternalStatus
  identifierValue: string
}

export function useGuidedDemographics(options: UseGuidedDemographicsOptions) {
  const {
    activeAssessmentPath, requestedAssessmentPath, assessmentDefinitions, identifierConfig, tracks, translate,
    age, ageUnit, sex, maternalStatus, identifierValue,
  } = options
  const editingDemographics = ref(false)
  let snapshot: GuidedModeDemographicsSnapshot | null = null

  const identifierPanelName = computed(() => identifierConfig.value?.panel || '')
  const identifierRequired = computed(() => identifierConfig.value?.required || false)
  const identifierPanel = computed(() => tracks.value
    .flatMap(item => item.panels || [])
    .find(panel => panel.name === identifierPanelName.value))
  const demographicAssessmentPath = computed(() => selectAssessmentPath({
    age: age.value,
    ageUnit: ageUnit.value,
    sex: sex.value,
    maternalStatus: maternalStatus.value,
  }))
  const proposedAssessmentPath = computed(() => (
    requestedAssessmentPath?.value || demographicAssessmentPath.value
  ))
  const demographicsDefinition = computed(() => {
    const path = activeAssessmentPath.value || proposedAssessmentPath.value
    return path ? assessmentDefinitions.value[path] || null : null
  })
  const identifierDefinitionEntry = computed(() => identifierPanelName.value
    ? findDemographicQuestion(demographicsDefinition.value, `concept.${identifierPanelName.value}`)
    : null)
  const ageDefinitionEntry = computed(() => findDemographicQuestion(demographicsDefinition.value, 'concept.age'))
  const sexDefinitionEntry = computed(() => findDemographicQuestion(demographicsDefinition.value, 'concept.gender'))
  const identifierValidationPattern = computed(() => identifierDefinitionEntry.value?.step.collection?.validation
    || identifierPanel.value?.options?.inputValidation || '')
  const identifierTitleToken = computed(() => identifierPanelName.value === 'patient_id'
    ? 'app.assessment_mode.guided.patient_id'
    : `questions.${identifierPanelName.value}.title`)
  const identifierLabel = computed(() => identifierPanelName.value
    ? identifierDefinitionEntry.value
      ? questionTitleFor(identifierDefinitionEntry.value.question)
      : translate(identifierTitleToken.value, formatDisplayLabel(identifierPanelName.value))
    : '')
  const identifierError = computed(() => identifierPanelName.value
    ? translate(`questions.${identifierPanelName.value}.error`, `Enter a valid ${identifierLabel.value}`)
    : '')
  const identifierHasValue = computed(() => identifierValue.value.trim().length > 0)
  const identifierMatchesValidation = computed(() => !identifierValidationPattern.value
    || matchesInputPattern(identifierValidationPattern.value, identifierValue.value))
  const identifierInvalid = computed(() => identifierHasValue.value && !identifierMatchesValidation.value)
  const identifierCanContinue = computed(() => !identifierPanelName.value
    || (!identifierRequired.value && !identifierHasValue.value)
    || (identifierHasValue.value && identifierMatchesValidation.value))

  const ageInDays = computed(() => convertAgeToDays(age.value, ageUnit.value))
  const ageDefinitionCollection = computed(() => ageDefinitionEntry.value?.step.collection)
  const ageDefinitionUsesSelectedUnit = computed(() => ageDefinitionCollection.value?.displayUnit === ageUnit.value)
  const ageMinimum = computed(() => ageDefinitionUsesSelectedUnit.value
    ? ageDefinitionCollection.value?.min ?? 1
    : 1)
  const ageMaximum = computed(() => ageDefinitionUsesSelectedUnit.value
    ? ageDefinitionCollection.value?.max ?? MAX_AGE_BY_UNIT[ageUnit.value]
    : MAX_AGE_BY_UNIT[ageUnit.value])
  const ageOutsideDefinitionBounds = computed(() => age.value !== undefined && ageDefinitionUsesSelectedUnit.value && (
    age.value < ageMinimum.value || age.value > ageMaximum.value
  ))
  const ageInvalid = computed(() => age.value !== undefined && (
    !isValidAge(age.value, ageUnit.value)
    || ageOutsideDefinitionBounds.value
    || (editingDemographics.value && activeAssessmentPath.value !== null
      && !isAgeWithinAssessmentPath(age.value, ageUnit.value, activeAssessmentPath.value))
  ))
  const collectMaternalStatus = computed(() => shouldCollectMaternalStatus(age.value, ageUnit.value, sex.value))
  const requestedPathMatchesDemographics = computed(() => !requestedAssessmentPath?.value
    || demographicAssessmentPath.value === requestedAssessmentPath.value)
  const assessmentUnavailable = computed(() => proposedAssessmentPath.value !== null
    && (!assessmentDefinitions.value[proposedAssessmentPath.value]
      || !tracks.value.some(item => item.path === proposedAssessmentPath.value)))
  const canStartAssessment = computed(() => demographicAssessmentPath.value !== null
    && requestedPathMatchesDemographics.value
    && proposedAssessmentPath.value !== null
    && !assessmentUnavailable.value
    && !ageInvalid.value
    && identifierCanContinue.value)
  const canSaveDemographics = computed(() => canStartAssessment.value
    && proposedAssessmentPath.value === activeAssessmentPath.value)
  const track = computed(() => tracks.value.find(item => item.path === activeAssessmentPath.value))
  const proposedTrack = computed(() => tracks.value.find(item => item.path === proposedAssessmentPath.value))
  const trackLabel = computed(() => assessmentPathLabel(activeAssessmentPath.value, track.value))
  const assessmentLabel = computed(() => assessmentPathLabel(proposedAssessmentPath.value, proposedTrack.value))
  const ageError = computed(() => {
    if (!editingDemographics.value || !activeAssessmentPath.value || !ageInvalid.value) return ''
    const assessment = trackLabel.value || formatDisplayLabel(activeAssessmentPath.value)
    return translate(
      'app.assessment_mode.guided.demographics.age_range_error',
      `Age must remain within the ${assessment} assessment range.`,
      { assessment },
    )
  })
  const sexEditingDisabled = computed(() => editingDemographics.value
    && (activeAssessmentPath.value === 'antenatal' || activeAssessmentPath.value === 'postnatal'))
  const maternalStatusEditingDisabled = computed(() => editingDemographics.value
    && (activeAssessmentPath.value === 'adult'
      || activeAssessmentPath.value === 'antenatal'
      || activeAssessmentPath.value === 'postnatal'))

  const demographicAgeTitle = computed(() => ageDefinitionEntry.value
    ? questionTitleFor(ageDefinitionEntry.value.question)
    : translate('questions.age.title', 'Age'))
  const demographicAgePrompt = computed(() => ageDefinitionEntry.value
    ? questionPromptFor(ageDefinitionEntry.value.question)
    : translate('questions.age.prompt', "What is the patient's age?"))
  const demographicSexPrompt = computed(() => sexDefinitionEntry.value
    ? questionPromptFor(sexDefinitionEntry.value.question)
    : translate('questions.gender.prompt', "What is the patient's sex?"))
  const demographicSexOptions = computed(() => {
    const question = sexDefinitionEntry.value?.question
    if (!question) {
      return SEX_DISPLAY_ORDER.map(value => ({
        value: value as GuidedPatientSex,
        labelToken: `responses.gender.${value}.title`,
      }))
    }
    const responseTokens = question.responseTokens || {}
    const concept = demographicsDefinition.value?.concepts[question.conceptId]
    const values = Object.keys(responseTokens).length
      ? Object.keys(responseTokens)
      : (concept?.value?.values || []).map(item => item.value)
    return SEX_DISPLAY_ORDER
      .filter(value => values.includes(value))
      .map(value => ({ value, labelToken: responseTokens[value] || `responses.gender.${value}.title` }))
  })
  const demographicItems = computed<GuidedModeDemographicItem[]>(() => {
    const items: GuidedModeDemographicItem[] = []
    // Maternal status is already conveyed by the selected maternal track.
    const maternalTrack = ['antenatal', 'postnatal'].includes(activeAssessmentPath.value || '')
    if (identifierLabel.value && identifierValue.value.trim()) {
      items.push({ id: 'identifier', label: identifierLabel.value, value: identifierValue.value.trim() })
    }
    if (age.value !== undefined) {
      items.push({
        id: 'age',
        label: demographicAgeTitle.value,
        value: `${age.value} ${translate(`units.${ageUnit.value}.short`, ageUnit.value)}`,
      })
    }
    if (sex.value) {
      items.push({
        id: 'sex',
        label: sexDefinitionEntry.value
          ? questionTitleFor(sexDefinitionEntry.value.question)
          : translate('questions.gender.title', 'Sex'),
        value: translate(
          sexDefinitionEntry.value?.question.responseTokens?.[sex.value] || `responses.gender.${sex.value}.title`,
          formatDisplayLabel(sex.value),
        ),
      })
    }
    if (collectMaternalStatus.value && maternalStatus.value && !maternalTrack) {
      items.push({
        id: 'maternal-status',
        label: translate('questions.maternal_status.title', 'Maternal Status'),
        value: translate(
          `responses.maternal_status.${maternalStatus.value}`,
          formatDisplayLabel(maternalStatus.value),
        ),
      })
    }
    return items
  })

  function questionTitleFor(question: GuidedModeQuestionDefinition) {
    return translate(question.titleToken, formatDisplayLabel(question.id.replace('question.', '')))
  }
  function questionPromptFor(question: GuidedModeQuestionDefinition) {
    return translate(question.promptToken, formatDisplayLabel(question.id.replace('question.', '')))
  }
  function assessmentPathLabel(path: GuidedAssessmentPath | null, configuredTrack?: GuidedModeTrack) {
    if (!path) return ''
    const patientTypeLabel = translate(`condition_patient_type.${path}`, formatDisplayLabel(path))
    if (path === 'adult' && sex.value === 'male') return patientTypeLabel
    return configuredTrack?.name
      ? translate(`tracks.${configuredTrack.name}.title`, patientTypeLabel)
      : patientTypeLabel
  }
  function clearRequestedPathForDemographicEdit() {
    if (activeAssessmentPath.value === null && requestedAssessmentPath?.value) {
      requestedAssessmentPath.value = null
    }
  }
  function setAge(value: number) {
    clearRequestedPathForDemographicEdit()
    age.value = Number.isFinite(value) && value > 0 ? value : undefined
  }
  function setAgeUnit(value: GuidedAgeUnit) {
    clearRequestedPathForDemographicEdit()
    ageUnit.value = value
  }
  function setSex(value: GuidedPatientSex) {
    if (sexEditingDisabled.value) return
    clearRequestedPathForDemographicEdit()
    sex.value = value
    if (editingDemographics.value && activeAssessmentPath.value === 'adult') {
      maternalStatus.value = 'none'
      return
    }
    if (!shouldCollectMaternalStatus(age.value, ageUnit.value, value)) maternalStatus.value = ''
  }
  function setMaternalStatus(value: GuidedMaternalStatus) {
    if (maternalStatusEditingDisabled.value) return
    clearRequestedPathForDemographicEdit()
    maternalStatus.value = value
  }
  function setIdentifier(value: string) {
    identifierValue.value = value
  }
  function beginEdit() {
    snapshot = {
      age: age.value,
      ageUnit: ageUnit.value,
      sex: sex.value,
      maternalStatus: maternalStatus.value,
      identifierValue: identifierValue.value,
    }
    if (activeAssessmentPath.value === 'adult') maternalStatus.value = 'none'
    editingDemographics.value = true
  }
  function acceptEdit() {
    editingDemographics.value = false
    snapshot = null
  }
  function cancelEdit() {
    if (snapshot) {
      age.value = snapshot.age
      ageUnit.value = snapshot.ageUnit
      sex.value = snapshot.sex
      maternalStatus.value = snapshot.maternalStatus
      identifierValue.value = snapshot.identifierValue
    }
    acceptEdit()
  }
  function answerValues(): Record<string, GuidedAnswer> | null {
    if (ageInDays.value === null) return null
    return {
      'concept.age': ageInDays.value,
      'concept.gender': sex.value,
      'concept.maternal_status': collectMaternalStatus.value ? maternalStatus.value : 'none',
    }
  }

  watch([ageInDays, sex], () => {
    const preserveLockedMaternalStatus = editingDemographics.value
      && (activeAssessmentPath.value === 'adult'
        || activeAssessmentPath.value === 'antenatal'
        || activeAssessmentPath.value === 'postnatal')
    if (!collectMaternalStatus.value && !preserveLockedMaternalStatus) maternalStatus.value = ''
  })

  return {
    editingDemographics,
    age,
    ageUnit,
    ageInDays,
    ageMinimum,
    ageMaximum,
    ageInvalid,
    ageError,
    demographicAgePrompt,
    demographicAgeTitle,
    sex,
    demographicSexPrompt,
    demographicSexOptions,
    maternalStatus,
    collectMaternalStatus,
    sexEditingDisabled,
    maternalStatusEditingDisabled,
    identifierPanelName,
    identifierLabel,
    identifierValue,
    identifierError,
    identifierInvalid,
    identifierHasValue,
    assessmentUnavailable,
    proposedAssessmentPath,
    canStartAssessment,
    canSaveDemographics,
    trackLabel,
    assessmentLabel,
    demographicItems,
    setAge,
    setAgeUnit,
    setSex,
    setMaternalStatus,
    setIdentifier,
    beginEdit,
    acceptEdit,
    cancelEdit,
    answerValues,
  }
}

function formatDisplayLabel(value: string) {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
}

function matchesInputPattern(pattern: string, value: string) {
  try {
    return new RegExp(pattern, pattern.includes('\\p') ? 'u' : undefined).test(value)
  } catch {
    return false
  }
}
